import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faCheck, faCopy, faRotateRight } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";

export interface SectionNavItem {
	key: string;
	label: string;
}

interface ResearchToolbarProps {
	sections: SectionNavItem[];
	activeSection: string | null;
	isCopied: boolean;
	isRerunDisabled: boolean;
	lastResearchedAt: string | null;
	creditLabel: string;
	onSectionClick: (key: string) => void;
	onCopy: () => void;
	onRerun: () => void;
}

/**
 * Section chips + the Copy / Re-run actions.
 *
 * The chip row never wraps — it scrolls horizontally instead (PREC-3680: the
 * chips have to fit one row), which is also why the labels are shortened
 * upstream.
 */
export const ResearchToolbar: FunctionComponent<ResearchToolbarProps> = ({
	sections,
	activeSection,
	isCopied,
	isRerunDisabled,
	lastResearchedAt,
	creditLabel,
	onSectionClick,
	onCopy,
	onRerun,
}) => {
	const { t } = useTranslation();

	return (
		<div className="flex flex-col gap-2">
			{sections.length > 1 && (
				<div
					className="flex flex-nowrap gap-1.5 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
					data-testid="research-section-nav"
				>
					{sections.map(({ key, label }) => (
						<button
							key={key}
							className={classNames(
								"shrink-0 cursor-pointer whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors",
								activeSection === key
									? "border-brand-light bg-light-blue text-brand-light"
									: "border-transparent bg-background-light text-text-meta hover:text-text"
							)}
							data-testid={`research-anchor-${key}`}
							type="button"
							onClick={() => onSectionClick(key)}
						>
							{label}
						</button>
					))}
				</div>
			)}

			<div className="flex items-center gap-2">
				<button
					className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg border border-border-light bg-white px-2.5 text-xs font-semibold text-text hover:border-brand-light"
					data-testid="research-copy"
					type="button"
					onClick={onCopy}
				>
					<Icon className="h-3 w-3" icon={isCopied ? faCheck : faCopy} />
					{isCopied
						? t("profileTracker.deepResearch.copied")
						: t("profileTracker.deepResearch.copy")}
				</button>

				<Tooltip
					content={
						lastResearchedAt
							? t("profileTracker.deepResearch.lastResearchedAt", { date: lastResearchedAt })
							: t("profileTracker.deepResearch.rerun")
					}
					variant="dark"
				>
					<button
						className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg border border-brand-light bg-light-blue px-2.5 text-xs font-semibold text-brand-light hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
						data-testid="research-rerun"
						disabled={isRerunDisabled}
						type="button"
						onClick={onRerun}
					>
						<Icon className="h-3 w-3" icon={faRotateRight} />
						{t("profileTracker.deepResearch.rerun")}
						<span className="rounded border border-brand-light bg-white px-1 text-[10px] leading-4">
							{creditLabel}
						</span>
					</button>
				</Tooltip>
			</div>
		</div>
	);
};
